import {getListOperation, GetOperation} from '@/service/operation/GetOperation'
import Result from '@/service/operation/Result'
import {ActionContext, Module} from 'vuex'
import {AppState} from '@/stores'
import {I18n} from 'vue-i18n'
import {LocalStore} from '@/service/store/LocalStore'
import {SiteManagerOrderApi, ReasonDto} from '@/service/api/sitemanager/SiteManagerOrderApi'
import {CustomerOrderBySiteBean, CustomerOrderDetailBean, MaterialDeliveryNoteBean} from '@/service/model/sitemanager/CustomerOrderBean'

import {DeliveryNoteState} from '@/stores/modules/DeliveryNoteModule'
import {SiteManagerQuotationRequestModuleState} from '@/stores/modules/sitemanager/SiteManagerQuotationRequestModule'
import {DeliveryNoteBean} from '@/service/model/DeliveryNoteBean'

const currentOrderDeliveryNotesStoreKey = 'currentOrderDeliveryNotes'

export interface SiteManagerOrderModuleState {
  ordersResult?: Result<Array<CustomerOrderBySiteBean>>
  currentOrderResult?: Result<CustomerOrderDetailBean>
  currentOrderDeliveryNotesOperation?: GetOperation<Array<DeliveryNoteBean>, string>
  currentOrderDeliveryNotesResult?: Array<DeliveryNoteBean>
  materialDeliveryNotes?: Array<MaterialDeliveryNoteBean>
}

export class SiteManagerOrderModule implements Module<SiteManagerOrderModuleState, AppState> {
  namespaced = true
  actions = {
    fetchOrders: this.fetchOrdersAction.bind(this),
    deleteOrder: this.deleteOrderAction.bind(this),
    refreshOrders: this.refreshOrdersAction.bind(this),
    
    selectOrder: this.selectOrderAction.bind(this),
    fetchOrderDetail: this.fetchOrderDetailAction.bind(this),
    loadMoreDeliveryNotes: this.loadMoreDeliveryNotesAction.bind(this),
    fetchMaterialDeliveryNotes: this.fetchMaterialDeliveryNotesAction.bind(this),
    
    clearOrderModule: this.clearOrderModuleAction.bind(this),
  }
  mutations = {
    setOrdersResult: (state, result) => state.ordersResult = result,
    setCurrentOrderResult: (state, result) => state.currentOrderResult = result,
    setCurrentOrderDeliveryNotesOperation: (state, operation) => state.currentOrderDeliveryNotesOperation = operation,
    setCurrentOrderDeliveryNotesResult: (state, result) => state.currentOrderDeliveryNotesResult = result,
    setMaterialDeliveryNotes: (state, notes) => state.materialDeliveryNotes = notes,
  }
  getters = {
    ordersResult: (state) => state.ordersResult,
    currentOrderResult: (state) => state.currentOrderResult,
    currentOrderDeliveryNotesResult: (state) => state.currentOrderDeliveryNotesResult,
    materialDeliveryNotes: (state) => state.materialDeliveryNotes,
  }
  
  constructor(
    private readonly i18n: I18n,
    private readonly listStore: LocalStore<Array<string>, string>,
    private readonly deliveryNoteStore: LocalStore<DeliveryNoteBean, string>,
    private readonly orderStore: LocalStore<any, string>,
    private readonly orderApi: SiteManagerOrderApi,
  ) {
  }
  
  private async loadMoreDeliveryNotesAction(
    context: ActionContext<SiteManagerOrderModuleState, AppState>,
    params  : {orderId: string}) {
    const deliveryNotes = await this.orderApi.fetchDeliveryNotes(params.orderId)
    context.commit('setCurrentOrderDeliveryNotesResult', deliveryNotes)
  }
  private async fetchOrdersAction(
    context: ActionContext<SiteManagerOrderModuleState, AppState>
  ): Promise<Array<CustomerOrderBySiteBean> | null> {
    try {
      const data = await this.orderApi.fetchOrdersV2()
      const result = new Result<Array<CustomerOrderBySiteBean>>(undefined, data)
      context.commit('setOrdersResult', result)
      return data
    } catch (error) {
      const result = new Result<Array<CustomerOrderBySiteBean>>(undefined, undefined, error)
      context.commit('setOrdersResult', result)
      throw error
    }
  }

  async refreshOrdersAction(
    context: ActionContext<SiteManagerOrderModuleState, AppState>
  ): Promise<Array<CustomerOrderBySiteBean> | null> {
    return this.fetchOrdersAction(context)
  }

  private async fetchOrderDetailAction(
    _context: ActionContext<SiteManagerOrderModuleState, AppState>, // eslint-disable-line @typescript-eslint/no-unused-vars
    orderId: string
  ): Promise<CustomerOrderDetailBean> {
    return this.orderApi.fetchOrderByIdV2(orderId)
  }
  
  private async selectOrderAction(
    context: ActionContext<DeliveryNoteState, AppState>,
    orderId: string
  ): Promise<any> {
    context.commit('setCurrentOrderResult', undefined)
    context.commit('setCurrentOrderDeliveryNotesResult', undefined)

    const deliveryNotesOperation = getListOperation(
      this.deliveryNoteStore,
      this.listStore,
      currentOrderDeliveryNotesStoreKey,
      (it: DeliveryNoteBean) => it.id,
      () => this.orderApi.fetchDeliveryNotes(orderId)
    )
    context.commit('setCurrentOrderDeliveryNotesOperation', deliveryNotesOperation)

    const orderPromise = this.orderApi.fetchOrderByIdV2(orderId).then(data => {
      context.commit('setCurrentOrderResult', new Result<CustomerOrderDetailBean>(undefined, data))
    }).catch(error => {
      context.commit('setCurrentOrderResult', new Result<CustomerOrderDetailBean>(undefined, undefined, error))
      throw error
    })

    const deliveryNotesPromise = deliveryNotesOperation.get(
      {params: null},
      it => context.commit('setCurrentOrderDeliveryNotesResult', it.item())
    )

    return Promise.all([orderPromise, deliveryNotesPromise])
  }
  
  clearOrderModuleAction(
    context: ActionContext<SiteManagerQuotationRequestModuleState, AppState>
  ) {
    context.commit('setOrdersResult', undefined)
    context.commit('setCurrentOrderResult', undefined)
    context.commit('setCurrentOrderDeliveryNotesOperation', undefined)
    context.commit('setCurrentOrderDeliveryNotesResult', undefined)
    context.commit('setMaterialDeliveryNotes', undefined)
    this.listStore.deleteItem(currentOrderDeliveryNotesStoreKey)
    this.orderStore.clear()
  }

  private async fetchMaterialDeliveryNotesAction(
    context: ActionContext<SiteManagerOrderModuleState, AppState>,
    params: {orderId: string; materialId: string}
  ): Promise<void> {
    context.commit('setMaterialDeliveryNotes', undefined)
    const notes = await this.orderApi.fetchMaterialDeliveryNotes(params.orderId, params.materialId)
    const sorted = [...notes].sort((a, b) => {
      if (a.deliveryDate && b.deliveryDate) {
        return new Date(b.deliveryDate).getTime() - new Date(a.deliveryDate).getTime()
      }
      return b.deliveryNoteReference.localeCompare(a.deliveryNoteReference)
    })
    context.commit('setMaterialDeliveryNotes', sorted)
  }

  async deleteOrderAction(
    context: ActionContext<SiteManagerOrderModuleState, AppState>,
    params : {orderId: string; reason: ReasonDto}
  ) {
    if (!params || !params.reason) {
      throw new Error('Reason is required')
    }
 
    try {
      await this.orderApi.deleteOrder({orderId : params.orderId, reason : params.reason})
    } catch (error) {
      console.error('Error requesting deleteOrder :', error)
      throw error
    }
  }
}
